// (LiveIndicator is defined at the bottom of this file.)

/**
 * Live Scoring Phase - Main orchestrator component
 * Handles role-based views for live scoring, and supports MULTIPLE concurrent
 * live matches (parallel turfs). When 2+ fixtures are 'live', user first picks one.
 */
import { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../../config/axios';
import { toast } from 'sonner';

import ScorerConsole from './ScorerConsole';
import LiveScoreView from './LiveScoreView';
import MatchStatsView from './MatchStatsView';
import PlayerStatsPanel from '../player-stats/PlayerStatsPanel';
import useLiveMatchSocket from './useLiveMatchSocket';

export default function LivePhase({ phase, event }) {
  const [liveFixtures, setLiveFixtures] = useState([]);   // all 'live' fixtures
  const [liveScore, setLiveScore] = useState(null);
  const [innings, setInnings] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('live'); // 'live', 'scorer', 'stats'
  const [currentUser, setCurrentUser] = useState(null);
  const [isScorer, setIsScorer] = useState(false);
  const [activeFixtureId, setActiveFixtureId] = useState(phase.fixture_id || null);
  const [forcePicker, setForcePicker] = useState(false);

  // Admin tools only for this event's admins (owner, co-admins, Super Admins)
  const isAdmin = !!event?.can_manage;

  const loadLiveFixtures = useCallback(async () => {
    try {
      const fixturesRes = await axiosInstance.get(`/v1/events/${event.id}/fixtures`);
      return (fixturesRes.data || []).filter(f => f.match_status === 'live');
    } catch {
      return [];
    }
  }, [event.id]);

  const fetchData = useCallback(async (pickFixtureId) => {
    try {
      setLoading(true);

      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try { setCurrentUser(JSON.parse(storedUser)); } catch (e) { console.error(e); }
      }

      // Determine which fixture(s) to show
      const liveList = await loadLiveFixtures();
      setLiveFixtures(liveList);

      let fixtureId = pickFixtureId || phase.fixture_id;

      if (!fixtureId) {
        // Use first live fixture as default if only one
        if (liveList.length === 1) fixtureId = liveList[0].id;
      }

      if (!fixtureId) {
        setActiveFixtureId(null);
        setLiveScore(null);
        setInnings([]);
        setStats([]);
        setLoading(false);
        return;
      }

      // Scorer access check for selected fixture
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          const scorersRes = await axiosInstance.get(
            `/v1/events/${event.id}/live/${fixtureId}/scorers`
          ).catch(() => ({ data: [] }));
          const isScorerAssigned = (scorersRes.data || []).some(s => s.user_id === user.id);
          setIsScorer(isScorerAssigned || !!event?.can_manage);
        } catch { /* noop */ }
      }

      const [scoreRes, inningsRes, statsRes] = await Promise.all([
        axiosInstance.get(`/v1/events/${event.id}/live/${fixtureId}/score`).catch(() => ({ data: null })),
        axiosInstance.get(`/v1/events/${event.id}/live/${fixtureId}/innings`).catch(() => ({ data: [] })),
        axiosInstance.get(`/v1/events/${event.id}/live/${fixtureId}/stats`).catch(() => ({ data: [] })),
      ]);

      setLiveScore(scoreRes.data);
      setInnings(inningsRes.data);
      setStats(statsRes.data);
      setActiveFixtureId(fixtureId);
    } catch (err) {
      console.error('Failed to fetch live data:', err);
    } finally {
      setLoading(false);
    }
  }, [event.id, event?.can_manage, phase.fixture_id, loadLiveFixtures]);

  // Subscribe to realtime updates (WebSocket + polling fallback + ping-based keep-alive)
  const { connected: wsConnected } = useLiveMatchSocket({
    eventId: event.id,
    fixtureId: activeFixtureId,
    enabled: !!activeFixtureId,
    onUpdate: (data) => handleWebSocketMessage(data || {}),
  });

  const handleWebSocketMessage = (data) => {
    switch (data.type) {
      case 'ball':
        if (data.data?.commentary) toast.info(data.data.commentary, { duration: 3000 });
        fetchData(activeFixtureId); break;
      case 'wicket':
        toast.error(`WICKET! ${data.data?.title || ''}`, { duration: 5000 });
        fetchData(activeFixtureId); break;
      case 'innings_end':
        toast.info(`Innings ${data.data?.innings_number} ended: ${data.data?.total}`, { duration: 5000 });
        fetchData(activeFixtureId); break;
      case 'super_over_start':
        toast.success(`Super Over: ${data.data?.batting_team} bat first`, { duration: 6000 });
        fetchData(activeFixtureId); break;
      case 'match_end':
        toast.success(`Match ended! ${data.data?.result_summary || data.data?.summary || ''}`, { duration: 10000 });
        fetchData(activeFixtureId); break;
      case 'poll':
        fetchData(activeFixtureId); break;
      default: break;
    }
  };

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3AAFA9]"></div>
      </div>
    );
  }

  // No live matches at all
  if (liveFixtures.length === 0 && !activeFixtureId) {
    return (
      <div className="text-center py-12" data-testid="no-live-match">
        <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
          <i className="ph-bold ph-broadcast text-2xl text-gray-400"></i>
        </div>
        <h3 className="text-lg font-bold text-gray-700 mb-2">No Live Match</h3>
        <p className="text-sm text-gray-500">
          Live scoring will be available when a match is in progress.
        </p>
      </div>
    );
  }

  // Multi-match picker: show list when 2+ live fixtures OR when user explicitly opened picker
  const showPicker = (liveFixtures.length > 1 && !activeFixtureId) || forcePicker;

  if (showPicker) {
    return (
      <div className="space-y-4" data-testid="live-match-picker">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-[#2B3A67] flex items-center gap-2">
              <i className="ph-bold ph-broadcast text-[#3AAFA9]"></i>
              Live Matches
              <span className="text-xs font-medium bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                {liveFixtures.length} LIVE
              </span>
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">Select a match to view or score.</p>
          </div>
          {activeFixtureId && (
            <button
              onClick={() => setForcePicker(false)}
              className="text-xs font-medium text-[#3AAFA9] hover:underline"
              data-testid="close-picker-btn"
            ><i className="ph-bold ph-x mr-1"></i>Close</button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {liveFixtures.map(fx => (
            <LiveMatchCard
              key={fx.id}
              fixture={fx}
              onSelect={() => { setForcePicker(false); fetchData(fx.id); }}
            />
          ))}
        </div>
      </div>
    );
  }

  const VIEW_TABS = [
    { key: 'live', label: 'Live Score', icon: 'ph-broadcast' },
    ...(isScorer || isAdmin ? [{ key: 'scorer', label: 'Scorer', icon: 'ph-note-pencil' }] : []),
    { key: 'stats', label: 'Match Stats', icon: 'ph-chart-bar' },
    { key: 'career', label: 'Player Stats', icon: 'ph-user-list' },
  ];

  const activeFixture = liveFixtures.find(f => f.id === activeFixtureId);

  return (
    <div className="space-y-4" data-testid="live-phase">
      {/* Multi-match switcher bar (appears when 2+ live matches) */}
      {liveFixtures.length > 1 && (
        <div className="bg-gradient-to-r from-[#2B3A67] to-[#3AAFA9]/90 rounded-xl p-3 flex items-center justify-between">
          <div className="text-white text-sm">
            <div className="flex items-center gap-2">
              <span className="relative inline-flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
              </span>
              <span className="font-bold">Now: </span>
              <span className="truncate">
                {activeFixture
                  ? `${activeFixture.team_a_name || 'Team A'} vs ${activeFixture.team_b_name || 'Team B'}`
                  : 'Select a match'}
                {activeFixture?.turf_name && ` · ${activeFixture.turf_name}`}
              </span>
            </div>
          </div>
          <button
            onClick={() => setForcePicker(true)}
            data-testid="switch-match-btn"
            className="text-xs font-bold bg-white text-[#2B3A67] px-3 py-1.5 rounded-lg hover:bg-white/90"
          >
            Switch ({liveFixtures.length})
          </button>
        </div>
      )}

      {activeFixtureId && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="flex items-center border-b border-gray-100">
            <div className="flex flex-1">
              {VIEW_TABS.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveView(tab.key)}
                  data-testid={`live-view-${tab.key}`}
                  className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                    activeView === tab.key
                      ? 'text-[#3AAFA9] border-b-2 border-[#3AAFA9] bg-[#3AAFA9]/5'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <i className={`ph-bold ${tab.icon}`}></i>
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              ))}
            </div>
            <LiveIndicator connected={wsConnected} data-testid="live-ws-indicator" />
          </div>

          <div className="p-4">
            {activeView === 'live' && (
              <LiveScoreView
                eventId={event.id}
                fixtureId={activeFixtureId}
                liveScore={liveScore}
                innings={innings}
                onRefresh={() => fetchData(activeFixtureId)}
              />
            )}
            {activeView === 'scorer' && (isScorer || isAdmin) && (
              <ScorerConsole
                eventId={event.id}
                event={event}
                fixtureId={activeFixtureId}
                innings={innings}
                currentUser={currentUser}
                onRefresh={() => fetchData(activeFixtureId)}
              />
            )}
            {activeView === 'stats' && (
              <MatchStatsView
                eventId={event.id}
                fixtureId={activeFixtureId}
                stats={stats}
              />
            )}
            {activeView === 'career' && (
              <PlayerStatsPanel eventId={event.id} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Mini card used in the multi-match picker ───────────────────────────────
function LiveMatchCard({ fixture, onSelect }) {
  const aScore = fixture.team_a_score != null ? `${fixture.team_a_score}/${fixture.team_a_wickets ?? 0}` : '—';
  const bScore = fixture.team_b_score != null ? `${fixture.team_b_score}/${fixture.team_b_wickets ?? 0}` : '—';
  return (
    <button
      onClick={onSelect}
      data-testid={`live-match-card-${fixture.id}`}
      className="text-left bg-white border border-gray-200 hover:border-[#3AAFA9] rounded-xl p-4 shadow-sm hover:shadow-md transition-all"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-red-600 uppercase tracking-wider">
          <span className="relative inline-flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
          </span>
          Live
        </span>
        <span className="text-[11px] text-gray-400">
          {fixture.turf_name || fixture.venue_name || ''}
        </span>
      </div>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="font-semibold text-gray-800 truncate">{fixture.team_a_name || 'Team A'}</span>
          <span className="font-bold text-[#2B3A67] tabular-nums">{aScore}
            {fixture.team_a_overs != null && <span className="text-[11px] text-gray-400 ml-1">({fixture.team_a_overs})</span>}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="font-semibold text-gray-800 truncate">{fixture.team_b_name || 'Team B'}</span>
          <span className="font-bold text-[#2B3A67] tabular-nums">{bScore}
            {fixture.team_b_overs != null && <span className="text-[11px] text-gray-400 ml-1">({fixture.team_b_overs})</span>}
          </span>
        </div>
      </div>
      <div className="mt-3 text-[11px] text-[#3AAFA9] font-medium flex items-center gap-1">
        <i className="ph-bold ph-arrow-right"></i> Open
      </div>
    </button>
  );
}

// ── Small live-connection badge ──────────────────────────────────────────
export function LiveIndicator({ connected }) {
  return (
    <div
      data-testid="live-ws-indicator"
      className={`hidden sm:flex items-center gap-1.5 mr-3 text-[11px] font-bold uppercase tracking-wider ${
        connected ? 'text-red-600' : 'text-gray-400'
      }`}
      title={connected ? 'Live updates connected' : 'Reconnecting… (auto-refreshing every 30s)'}
    >
      <span className="relative inline-flex h-2 w-2">
        {connected && (
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
        )}
        <span className={`relative inline-flex rounded-full h-2 w-2 ${connected ? 'bg-red-500' : 'bg-gray-300'}`}></span>
      </span>
      {connected ? 'Live' : 'Reconnect…'}
    </div>
  );
}

